import React, { useEffect, useRef, useState } from 'react'
import { BreathAPIs } from '../APIs/BreathAPIs'
import { ButtonCustom } from '../BootstrapComponents/ButtonCustom'
import PhoneInput from '../components/PhoneInput'
import LocationLookup from './LocationLookup'
import styles from './MFAValidation.module.css'

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const BRAND_COLOR = '#18365E';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sanitizePhoneNumber = (input) => {
  const sanitized = input.replace(/\D/g, '');
  return sanitized.startsWith('1') ? sanitized.slice(1) : sanitized;
};

const GuestOTPValidation = ({ onVerified, onBack }) => {

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [statusType, setStatusType] = useState('info');
  const [verifyToken, setVerifyToken] = useState('');
  const [otpDigits, setOtpDigits] = useState(new Array(OTP_LENGTH).fill(''));
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Defaults to true (OTP required) / false (email mandatory) until the admin
  // settings load, so the safer/current behavior is what shows if that call
  // is slow or fails.
  const [otpRequired, setOtpRequired] = useState(true);
  const [phoneMandatory, setPhoneMandatory] = useState(false);

  const inputRefs = useRef([]);
  const lastSubmittedOtp = useRef('');

  const otp = otpDigits.join('');

  useEffect(() => {
    BreathAPIs.getGuestSettings()
      .then(response => {
        if (response?.data?.otpRequired === false) {
          setOtpRequired(false);
        }
        if (response?.data?.phoneMandatory === true) {
          setPhoneMandatory(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Tags the already-selected location to the fresh session's token and
  // hands off to the parent's login dispatch/redirect.
  const finalizeLogin = (token, guestId) => {
    localStorage.setItem('selectedLocation', selectedLocation._id);
    localStorage.setItem('selectedLocationToken', token);
    onVerified(token, guestId);
  };

  // Admin has turned email OTP off - log the guest in directly off the email
  // (+ optional phone) alone, same as a successful OTP verify from here on.
  const loginWithoutOtp = () => {
    setSendingOtp(true);
    setStatusType('info');
    setVerificationStatus('');
    BreathAPIs.guestLoginDirect(email, phone)
      .then(response => {
        if (response && response.data && response.data.success && response.data.token && response.data.guestId) {
          finalizeLogin(response.data.token, response.data.guestId);
        } else {
          setStatusType('error');
          setVerificationStatus((response.data && response.data.message) || 'Failed to continue. Please try again.');
        }
      })
      .catch(error => {
        setStatusType('error');
        setVerificationStatus(error?.response?.data?.message || 'An error occurred. Please try again.');
      })
      .finally(() => setSendingOtp(false));
  };

  const requestOtp = () => {
    if (!selectedLocation) {
      setStatusType('error');
      setVerificationStatus('Please select a location.');
      return;
    }

    if (phoneMandatory) {
      if (phone.length !== 10) {
        setStatusType('error');
        setVerificationStatus('Please enter a valid 10-digit phone number.');
        return;
      }
      if (email && !EMAIL_REGEX.test(email)) {
        setStatusType('error');
        setVerificationStatus('Please enter a valid email address.');
        return;
      }
    } else {
      if (!EMAIL_REGEX.test(email)) {
        setStatusType('error');
        setVerificationStatus('Please enter a valid email address.');
        return;
      }
      if (phone && phone.length !== 10) {
        setStatusType('error');
        setVerificationStatus('Please enter a valid 10-digit phone number.');
        return;
      }
    }

    if (!otpRequired) {
      loginWithoutOtp();
      return;
    }

    setSendingOtp(true);
    setStatusType('info');
    setVerificationStatus('');
    BreathAPIs.guestSendOTP(email, phone)
      .then(response => {
        if (response && response.status === 200 && response.data && response.data.success) {
          setOtpSent(true);
          setOtpDigits(new Array(OTP_LENGTH).fill(''));
          lastSubmittedOtp.current = '';
          setStatusType('success');
          setVerificationStatus(phoneMandatory
            ? 'A 6-digit code has been sent to your phone.'
            : 'A 6-digit code has been sent to your email.');
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
          if (response.data.token) {
            setVerifyToken(response.data.token);
          }
          setTimeout(() => inputRefs.current[0]?.focus(), 0);
        } else {
          setStatusType('error');
          setVerificationStatus((response.data && response.data.message) || 'Failed to send the code. Please try again.');
        }
      })
      .catch(error => {
        setStatusType('error');
        setVerificationStatus(error?.response?.data?.message || 'An error occurred while sending the code. Please try again.');
      })
      .finally(() => setSendingOtp(false));
  };

  const verifyOtp = (code) => {
    setVerifying(true);
    setStatusType('info');
    setVerificationStatus('');
    BreathAPIs.guestVerifyOTP(code, verifyToken)
      .then(response => {
        if (response && response.data && response.data.success && response.data.token && response.data.guestId) {
          setStatusType('success');
          setVerificationStatus(phoneMandatory ? 'Phone verified successfully.' : 'Email verified successfully.');
          finalizeLogin(response.data.token, response.data.guestId);
        } else {
          setStatusType('error');
          setVerificationStatus((response.data && response.data.message) || 'Incorrect code. Please try again.');
          setOtpDigits(new Array(OTP_LENGTH).fill(''));
          lastSubmittedOtp.current = '';
          inputRefs.current[0]?.focus();
        }
      })
      .catch(error => {
        setStatusType('error');
        setVerificationStatus(
          error?.response?.data?.tokenExpired
            ? 'Code expired. Please request a new one.'
            : (error.response?.data?.message || 'Incorrect code. Please try again.')
        );
        setOtpDigits(new Array(OTP_LENGTH).fill(''));
        lastSubmittedOtp.current = '';
        inputRefs.current[0]?.focus();
      })
      .finally(() => setVerifying(false));
  };

  useEffect(() => {
    if (otp.length === OTP_LENGTH && lastSubmittedOtp.current !== otp) {
      lastSubmittedOtp.current = otp;
      verifyOtp(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleDigitChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    setOtpDigits(prev => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = new Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => { next[i] = char; });
    setOtpDigits(next);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconCircle}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L4 5V11C4 16.5 7.4 21.7 12 23C16.6 21.7 20 16.5 20 11V5L12 2Z" stroke="#18365E" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M9 12L11 14L15.5 9.5" stroke="#18365E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h5 className={styles.title}>Continue as Guest</h5>

      {!otpSent ? (
        <>
          <p className={styles.subtitle}>
            {phoneMandatory
              ? (otpRequired
                ? "Enter your phone number to continue as a guest. We'll text you a 6-digit code to verify it."
                : 'Enter your phone number to continue as a guest.')
              : (otpRequired
                ? "Enter your email to continue as a guest. We'll send a 6-digit code to verify it."
                : 'Enter your email to continue as a guest.')}
          </p>
          <div className="form-floating mb-3" style={{ width: '100%' }}>
            <input
              className="form-control"
              type="email"
              placeholder="Enter Email"
              value={email}
              disabled={sendingOtp}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus={!phoneMandatory}
            />
            <label>{phoneMandatory ? 'Email (optional)' : 'Email (required)'}</label>
          </div>
          <div className={`form-floating mb-3 input-left-box ${styles['input-left-box']}`} style={{ width: '100%' }}>
            <PhoneInput
              addCode={true}
              className="form-control"
              type="text"
              name="phone"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(sanitizePhoneNumber(e.target.value))}
            />
            <span className={styles['left-box-component']}>+1</span>
            <label>{phoneMandatory ? 'Phone (required)' : 'Phone (optional)'}</label>
          </div>
          <LocationLookup selectedLocationId={selectedLocation?._id} onSelectLocation={setSelectedLocation} />
          <ButtonCustom
            variant="primary"
            text={otpRequired
              ? (sendingOtp ? 'Sending Code...' : 'Send Code')
              : (sendingOtp ? 'Continuing...' : 'Continue')}
            isLoading={sendingOtp}
            onClick={requestOtp}
            style={{ width: '100%', backgroundColor: BRAND_COLOR, borderColor: BRAND_COLOR }}
          />
        </>
      ) : (
        <>
          <p className={styles.subtitle}>
            Enter the 6-digit code sent to <strong>{phoneMandatory ? `+1 ${phone}` : email}</strong>
          </p>

          <div className={styles.otpRow} onPaste={handlePaste}>
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                className={`${styles.otpBox} ${statusType === 'error' ? styles.otpBoxError : ''}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                disabled={verifying}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {verifying && (
            <div className={styles.verifyingRow}>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Verifying...
            </div>
          )}

          <div className={styles.resendRow}>
            {resendCooldown > 0 ? (
              <span className={styles.resendDisabled}>Resend code in {resendCooldown}s</span>
            ) : (
              <button
                type="button"
                className={styles.resendLink}
                onClick={requestOtp}
                disabled={sendingOtp}
              >
                {sendingOtp ? 'Resending...' : 'Resend code'}
              </button>
            )}
          </div>
        </>
      )}

      {verificationStatus && (
        <p className={`${styles.status} ${statusType === 'error' ? styles.statusError : statusType === 'success' ? styles.statusSuccess : ''}`}>
          {verificationStatus}
        </p>
      )}
      <div className={styles.backRow}>
        <button type="button" className={styles.backLink} onClick={onBack}>&larr; Back to Login</button>
      </div>
    </div>
  );
}
export default GuestOTPValidation
